import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

/**
 * M23 L23.06 — Document audit log.
 *
 * Reads `GET /v1/document-audit-logs` with optional event-type filter and
 * pagination. Every document lifecycle event is recorded here.
 */
interface AuditRow {
  readonly auditId: string;
  readonly eventType: string;
  readonly entityType: string;
  readonly entityId: string;
  readonly entityName: string | null;
  readonly action: string;
  readonly actorId: string | null;
  readonly actorName: string | null;
  readonly documentId: string | null;
  readonly eventTimestamp: string;
}

const PAGE = 25;

export function DocumentAuditLogPage(): ReactNode {
  const { at } = useBusinessDay();
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<AuditRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [eventType, setEventType] = useState('');
  const [offset, setOffset] = useState(0);

  const load = useCallback(async () => {
    setRows(null);
    setError(null);
    const params = new URLSearchParams({ limit: String(PAGE), offset: String(offset) });
    if (eventType.trim()) params.set('eventType', eventType.trim());
    try {
      const r = await apiRequest<{ data: AuditRow[]; total: number }>(
        `/v1/document-audit-logs?${params.toString()}`, { token },
      );
      setRows(r.data ?? []);
      setTotal(r.total ?? 0);
    } catch (e) {
      setError((e as Error).message);
      setRows([]);
    }
  }, [token, eventType, offset]);

  useEffect(() => { void load(); }, [load]);

  return (
    <main aria-label="document-audit-log">
      <h1 style={{ fontSize: 20, marginBottom: 4 }}>{t('m23.auditLog.title')}</h1>
      <p style={{ color: 'var(--color-text-muted)', marginBottom: 16, fontSize: 13 }}>{t('m23.auditLog.subtitle')}</p>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
        <input aria-label="event-type" placeholder={t('m23.auditLog.filterPlaceholder')} value={eventType}
               onChange={(e) => setEventType(e.target.value)}
               style={{ padding: '5px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
        <button type="button" data-testid="search-btn" onClick={() => { setOffset(0); void load(); }}
                style={{ padding: '5px 12px', borderRadius: 4, border: '1px solid var(--color-border-default)' }}>{t('m23.auditLog.search')}</button>
      </div>

      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {!rows ? (
        <p>{t('m23.auditLog.loading')}</p>
      ) : rows.length === 0 ? (
        <p data-testid="audit-empty" style={{ color: 'var(--color-text-muted)' }}>{t('m23.auditLog.empty')}</p>
      ) : (
        <>
          <table aria-label="audit-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--color-border-default)', textAlign: 'left' }}>
                <th style={{ padding: '8px 4px' }}>{t('m23.auditLog.colTimestamp')}</th>
                <th style={{ padding: '8px 4px' }}>{t('m23.auditLog.colEvent')}</th>
                <th style={{ padding: '8px 4px' }}>{t('m23.auditLog.colEntity')}</th>
                <th style={{ padding: '8px 4px' }}>{t('m23.auditLog.colAction')}</th>
                <th style={{ padding: '8px 4px' }}>{t('m23.auditLog.colActor')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.auditId} data-testid={`audit-row-${r.auditId}`} style={{ borderBottom: '1px solid var(--color-border-default)' }}>
                  <td style={{ padding: '8px 4px' }}>{at(r.eventTimestamp)}</td>
                  <td style={{ padding: '8px 4px' }}>{r.eventType}</td>
                  {/* The record this row is about, by name. It used to read
                      "T296DocumentMaster · <uuid>" — an internal class name
                      and an id, neither of which names a document. */}
                  <td style={{ padding: '8px 4px' }}>{r.entityName ?? '—'}</td>
                  <td style={{ padding: '8px 4px' }}>{r.action}</td>
                  <td style={{ padding: '8px 4px' }}>{r.actorName ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
            <button type="button" data-testid="prev-page" disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - PAGE))}
                    style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid var(--color-border-default)' }}>{t('m23.auditLog.prev')}</button>
            <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
              {t('m23.auditLog.pageInfo', { from: offset + 1, to: offset + rows.length, total })}
            </span>
            <button type="button" data-testid="next-page" disabled={offset + PAGE >= total}
                    onClick={() => setOffset(offset + PAGE)}
                    style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid var(--color-border-default)' }}>{t('m23.auditLog.next')}</button>
          </div>
        </>
      )}
    </main>
  );
}
